import { getSupabase } from "../components/supabaseClient";
import {
  tableName,
  submissions_arrayUrls,
  singleLinkSubmissionUrl,
  getWTFFormsConfig,
  BASE_URLS,
} from "../components/env.js";
import { withAIQueryTracking, trackUserEvent } from "./userEventTracker";

/**
 * Enhanced function to detect if a form URL represents a filled form
 * This now accepts all form URLs, including those that don't contain /submissions/ or /submission/
 * @param {string} url - The form URL to check
 * @returns {boolean} - True if the form URL is valid, false otherwise
 */
const isFormFilled = (url) => {
  if (!url) return false;

  // Check if URL is a valid string and not empty/whitespace only
  const trimmedUrl = url.trim();
  if (!trimmedUrl) return false;

  // Accept all non-empty URLs as valid forms
  // This includes:
  // - Form URLs with /submissions/ or /submission/ (filled forms)
  // - Form URLs without these patterns (blank forms, static forms, etc.)
  // - Any other valid URL that represents a form

  return true;
};

/**
 * Extracts all submission URLs from a record's two stored submission columns,
 * without a request: extractAllSubmissionLinks reads one record and passes its
 * columns here, so a caller already holding them (one batched read for a whole
 * table) gets the same links for the same values.
 * @param {string} scheduleId - The record's id, used to build WTF_forms URLs
 * @param {*} rawArray - The record's `submissions_arrayUrls` column value
 * @param {*} singleLink - The record's `singleLinkSubmissionUrl` column value
 * @returns {string[]}
 */
export const submissionLinksFromValues = (scheduleId, rawArray, singleLink) => {
  // console.log("[SUBMISSION DEBUG] Raw submissions data:", rawArray);

  // If the array column is empty or null, check the single link column
  if (!rawArray || (Array.isArray(rawArray) && rawArray.length === 0)) {
    // console.log(
    //   "[SUBMISSION DEBUG] No array data, checking single link:",
    //   singleLink
    // );
    // Check if single link represents a filled form
    if (singleLink && isFormFilled(singleLink)) {
      // console.log(
      //   "[SUBMISSION DEBUG] Single link represents a filled form:",
      //   singleLink
      // );
      return [singleLink];
    }
    // console.log(
    //   "[SUBMISSION DEBUG] Single link is not a filled form or missing"
    // );
    return [];
  }

  const submissions = Array.isArray(rawArray) ? rawArray : [rawArray];
  // console.log("[SUBMISSION DEBUG] Processed submissions array:", submissions);

  if (submissions.length === 0) {
    return [];
  }

  // Map the array to a new array containing only the URLs and filter out blank forms
  // Skip Epic-sourced entries — those are handled separately via extractEpicFormEntries
  const extractedUrls = submissions
    .filter((form) => form?.source !== 'epic')
    .map((form) => {
      let url = form.url;

      // If no URL exists, construct one using WTF_forms environment configuration
      if (!url && (form.id || form.submission_id)) {
        try {
          const config = getWTFFormsConfig();
          url = `${config.baseURL}${
            config.formsPath
          }/${scheduleId}/submission/${form.id || form.submission_id}`;
        } catch (error) {
          console.warn(
            "[SUBMISSION DEBUG] Error constructing URL with WTF_forms config:",
            error
          );
          // Fallback to hardcoded URL for backward compatibility
          url = `http://127.0.0.1:5001/${scheduleId}/submission/${
            form.id || form.submission_id
          }`;
        }
      }

      // console.log(
      //   "[SUBMISSION DEBUG] Extracting URL from form:",
      //   form,
      //   "->",
      //   url,
      //   "(Filled:",
      //   isFormFilled(url),
      //   ")"
      // );
      return url;
    })
    .filter((url) => {
      // Only include URLs that represent filled forms
      const filled = isFormFilled(url);
      // console.log(
      //   "[SUBMISSION DEBUG] Filtering URL:",
      //   url,
      //   "- Included:",
      //   filled
      // );
      return filled;
    });

  // console.log("[SUBMISSION DEBUG] Final extracted URLs:", extractedUrls);
  return extractedUrls;
};

/**
 * Fetches and extracts all submission URLs for a single record from the database.
 * It first checks for an array of submissions and falls back to a single submission URL if the array is empty.
 * Returns all valid form URLs, including both filled forms and blank forms.
 * @param {string} scheduleId
 * @param {string} [schema] - Optional Postgres schema to scope the lookup to
 *   (cross-schema / linked-provider patients). When omitted, behavior is
 *   byte-identical to before this param existed (plain client, no `.schema()`).
 * @returns {Promise<string[]>}
 */
/** Supabase `id` on schedules/notes is a UUID; display IDs like "123456" are not valid */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const extractAllSubmissionLinks = async (scheduleId, schema) => {
  if (!scheduleId) {
    console.warn(
      "[SUBMISSION DEBUG] No scheduleId provided to extractAllSubmissionLinks."
    );
    return [];
  }

  if (typeof scheduleId === "string" && !UUID_RE.test(scheduleId.trim())) {
    console.warn(
      "[SUBMISSION DEBUG] scheduleId is not a UUID — skipping submission link DB lookup:",
      scheduleId,
    );
    return [];
  }

  try {
    // console.log(
    //   "[SUBMISSION DEBUG] Extracting submission links for:",
    //   scheduleId
    // );
    // console.log("[SUBMISSION DEBUG] Using table:", tableName);
    // console.log(
    //   "[SUBMISSION DEBUG] Using columns:",
    //   submissions_arrayUrls,
    //   singleLinkSubmissionUrl
    // );

    const supabase = await getSupabase();
    const client = schema ? supabase.schema(schema) : supabase;

    // Use environment variables for table name and selected columns
    const { data, error } = await client
      .from(tableName)
      .select(`${submissions_arrayUrls}, ${singleLinkSubmissionUrl}`)
      .eq("id", scheduleId)
      .maybeSingle();

    // console.log("[SUBMISSION DEBUG] Query result:", { data, error });

    if (error) {
      console.error(
        "[SUBMISSION DEBUG] Error fetching submission links from Supabase:",
        error
      );
      return [];
    }

    // The array column first, the single link column when it is empty.
    return submissionLinksFromValues(
      scheduleId,
      data?.[submissions_arrayUrls],
      data?.[singleLinkSubmissionUrl]
    );
  } catch (err) {
    console.error(
      "[SUBMISSION DEBUG] An unexpected error occurred while extracting submission links:",
      err
    );
    return [];
  }
};

/**
 * What extractAllSubmissionLinks answers for a record whose two submission
 * columns were already read, e.g. by one batched read for a whole table.
 * Like it, answers [] for an id it would not look up (missing, or a string
 * that is not a UUID), for a record the read did not return, and for a
 * stored value that cannot be extracted.
 * @param {string} scheduleId
 * @param {Object|null|undefined} row - The record as read, with both columns
 * @returns {string[]}
 */
export const submissionLinksFromRow = (scheduleId, row) => {
  if (!scheduleId || !row) return [];
  if (typeof scheduleId === "string" && !UUID_RE.test(scheduleId.trim())) {
    return [];
  }
  try {
    return submissionLinksFromValues(
      scheduleId,
      row[submissions_arrayUrls],
      row[singleLinkSubmissionUrl]
    );
  } catch (err) {
    console.error(
      "[SUBMISSION DEBUG] An unexpected error occurred while extracting submission links:",
      err
    );
    return [];
  }
};

/**
 * Enhanced function to check if a patient has any filled forms
 * @param {string} scheduleId - The patient's schedule ID
 * @returns {Promise<boolean>} - True if patient has filled forms, false otherwise
 */
export const hasFilledForms = async (scheduleId) => {
  const submissionLinks = await extractAllSubmissionLinks(scheduleId);
  return submissionLinks && submissionLinks.length > 0;
};

/**
 * Gets the count of filled forms for a patient
 * @param {string} scheduleId - The patient's schedule ID
 * @returns {Promise<number>} - Number of filled forms
 */
export const getFilledFormCount = async (scheduleId) => {
  const submissionLinks = await extractAllSubmissionLinks(scheduleId);
  return submissionLinks ? submissionLinks.length : 0;
};

/**
 * Adds a form URL to the patient's submissions_array column
 * @param {string} patientId - The patient's ID/UUID
 * @param {string} formUrl - The URL of the generated form
 * @param {Object} options - Additional options for form submission
 * @param {string} options.formId - The WTF_forms form ID (if applicable)
 * @param {string} options.submissionId - The WTF_forms submission ID (if applicable)
 * @param {boolean} options.isWTFForm - Flag to indicate if this is a WTF_form
 * @param {string} [schema] - Optional Postgres schema to scope the read/write
 *   to (cross-schema / linked-provider patients). Omitted => byte-identical
 *   to before this param existed.
 * @returns {Promise<boolean>} - True if successful, false otherwise
 */
export const addFormToSubmissionsArray = async (
  patientId,
  formUrl,
  options = {},
  schema
) => {
  try {
    if (!patientId || !formUrl) {
      console.error("[SUBMISSION DEBUG] Missing required parameters:", {
        patientId,
        formUrl,
      });
      return false;
    }

    const { formId, submissionId, isWTFForm } = options;

    // Ensure patientId is a string for consistent handling
    patientId = String(patientId);

    // console.log("[SUBMISSION DEBUG] Environment variables:", {
    //   tableName,
    //   submissions_arrayUrls,
    // });

    const supabase = await getSupabase();
    const client = schema ? supabase.schema(schema) : supabase;

    // console.log("[SUBMISSION DEBUG] Adding form to submissions array:", {
    //   patientId,
    //   formUrl,
    //   formId,
    //   submissionId,
    //   isWTFForm,
    // });

    // First, get the current submissions_array for the patient
    // console.log("[SUBMISSION DEBUG] Fetching patient data for ID:", patientId);
    const { data: patientData, error: fetchError } = await client
      .from(tableName)
      .select(submissions_arrayUrls)
      .eq("id", patientId)
      .single();

    // console.log("[SUBMISSION DEBUG] Fetch result:", {
    //   patientData,
    //   fetchError,
    // });

    if (fetchError) {
      console.error(
        "[SUBMISSION DEBUG] Error fetching patient submissions:",
        fetchError
      );
      return false;
    }

    // Get current submissions array or initialize as empty array
    let currentSubmissions = patientData?.[submissions_arrayUrls] || [];
    // console.log(
    //   "[SUBMISSION DEBUG] Current submissions array:",
    //   currentSubmissions
    // );

    // Ensure it's an array
    if (!Array.isArray(currentSubmissions)) {
      currentSubmissions = currentSubmissions ? [currentSubmissions] : [];
    }

    // Enforce a maximum of 3 submissions
    if (currentSubmissions.length >= 3) {
      // Sort by date to find the oldest submission
      // Submissions without 'added_at' will be treated as the oldest
      currentSubmissions.sort((a, b) => {
        const dateA = a.added_at ? new Date(a.added_at) : new Date(0); // Treat missing date as very old
        const dateB = b.added_at ? new Date(b.added_at) : new Date(0);
        return dateA - dateB;
      });
      // Remove the oldest submission
      currentSubmissions.shift();
      // console.log(
      //   "[SUBMISSION DEBUG] Removed oldest submission to maintain a maximum of 3."
      // );
    }

    // Check if this form URL already exists in submissions
    const formExists = currentSubmissions.some(
      (submission) => submission.url === formUrl
    );

    if (formExists) {
      // console.log(
      //   "[SUBMISSION DEBUG] Form already exists in submissions:",
      //   formUrl
      // );
      return true; // Consider it successful since it's already there
    }

    // Add the new form to the submissions array
    const newSubmission = {
      url: formUrl,
      added_at: new Date().toISOString(),
      type: isWTFForm
        ? submissionId
          ? "wtf_form_submission"
          : "wtf_form"
        : "generated_form",
      form_id: isWTFForm ? formId : null,
      submission_id: isWTFForm ? submissionId : null,
    };

    const updatedSubmissions = [...currentSubmissions, newSubmission];
    // console.log(
    //   "[SUBMISSION DEBUG] Updated submissions array:",
    //   updatedSubmissions
    // );

    // Update the patient's submissions_array
    // console.log("[SUBMISSION DEBUG] Updating patient record with:", {
    //   tableName,
    //   patientId,
    //   updateField: submissions_arrayUrls,
    //   updateData: updatedSubmissions,
    // });

    const { error: updateError } = await client
      .from(tableName)
      .update({ [submissions_arrayUrls]: updatedSubmissions })
      .eq("id", patientId);

    // console.log("[SUBMISSION DEBUG] Update result:", { updateError });

    if (updateError) {
      console.error(
        "[SUBMISSION DEBUG] Error updating submissions array:",
        updateError
      );
      return false;
    }

    // console.log(
    //   "[SUBMISSION DEBUG] Successfully added form to submissions array:",
    //   {
    //     patientId,
    //     formUrl,
    //     totalSubmissions: updatedSubmissions.length,
    //   }
    // );

    // Trigger form processing if this is a WTF form submission
    if (isWTFForm && submissionId) {
      try {
        // console.log(
        //   "[FORM PROCESSING] Triggering automatic form processing for submission:",
        //   { formId, submissionId }
        // );

        // Get backend URL
        const hostname = window.location.hostname;
        const backendUrl =
          hostname === "localhost" || hostname === "127.0.0.1"
            ? BASE_URLS.local.backend
            : process.env.REACT_APP_BACKEND_URL || BASE_URLS.render.backend;

        // Call the form processing endpoint (fire and forget - don't wait for response)
        withAIQueryTracking('form_gen', () => fetch(`${backendUrl}/api/ai-assistant/process-form-submission/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            form_id: formId,
            submission_id: submissionId,
          }),
        }))
          .then((response) => response.json())
          .then((data) => {
            // console.log(
            //   "[FORM PROCESSING] Form processing triggered successfully:",
            //   data
            // );
          })
          .catch((error) => {
            console.warn(
              "[FORM PROCESSING] Failed to trigger form processing (non-critical):",
              error
            );
            // Don't throw - this is a background process
          });
      } catch (error) {
        console.warn(
          "[FORM PROCESSING] Error triggering form processing (non-critical):",
          error
        );
        // Don't throw - form was still added successfully
      }
    }

    trackUserEvent('form_submit', {
      metadata: { form_id: formId || null, is_wtf_form: !!isWTFForm },
    });

    return true;
  } catch (error) {
    console.error(
      "[SUBMISSION DEBUG] Error in addFormToSubmissionsArray:",
      error
    );
    return false;
  }
};

/**
 * Adds a WTF_form submission to the patient's submissions_array column
 * @param {string} patientId - The patient's ID/UUID
 * @param {string} formId - The WTF_forms form ID
 * @param {string} submissionId - The WTF_forms submission ID
 * @returns {Promise<boolean>} - True if successful, false otherwise
 */
/**
 * Returns the raw Epic form entries from a patient's submissions_array
 * (entries where source === 'epic'). These are not URL-based — they reference
 * rows in StoringFormSubmission by form_id/submission_id.
 *
 * @param {string} scheduleId - The patient's UUID
 * @param {string} [schema] - Optional Postgres schema to scope the lookup to
 *   (cross-schema / linked-provider patients). Omitted => byte-identical to
 *   before this param existed.
 * @returns {Promise<Array<{form_id, submission_id, source}>>}
 */
export const extractEpicFormEntries = async (scheduleId, schema) => {
  if (!scheduleId) return [];
  try {
    const supabase = await getSupabase();
    const client = schema ? supabase.schema(schema) : supabase;
    const { data, error } = await client
      .from(tableName)
      .select(submissions_arrayUrls)
      .eq("id", scheduleId)
      .maybeSingle();
    if (error || !data) return [];
    const arr = data?.[submissions_arrayUrls];
    if (!Array.isArray(arr)) return [];
    return arr.filter((e) => e?.source === 'epic' && e?.form_id);
  } catch (err) {
    console.warn("[SUBMISSION DEBUG] extractEpicFormEntries error:", err);
    return [];
  }
};

export const addWTFFormSubmissionToArray = async (
  patientId,
  formId,
  submissionId = null,
  schema
) => {
  // Use the new WTF_forms environment configuration
  const config = getWTFFormsConfig();
  const formUrl = submissionId
    ? `${config.baseURL}${config.formsPath}/${formId}/submission/${submissionId}`
    : `${config.baseURL}${config.formsPath}/${formId}`;
  return await addFormToSubmissionsArray(patientId, formUrl, {
    formId,
    submissionId,
    isWTFForm: true,
  }, schema);
};
